/**
 * Modelo de parâmetro chave-valor retornado por
 * Administradora/ParametrosResumidos/{tenantSlug}/1 e
 * Administradora/ParametrosCompletos/{tenantSlug}/1 (host `urlApiLaserchip`,
 * ver src/environments/endpoints.ts). CONFIRMADO lendo a resposta real do
 * endpoint para o tenant `conectaplan` (24/08/2026, dados de configuração do
 * tenant — CNPJ, endereço, cores, URLs de imagens —, não dados pessoais de
 * usuário): a resposta é `{ ...camposFixos, parametros: [{ sParametro,
 * sValor, sTipo, sDscParametro, sTamanhoImagem }] }` — os parâmetros
 * flexíveis vêm numa lista à parte dos campos fixos do cadastro da
 * administradora, bem diferente do modelo `Enterprise` totalmente fixo do
 * portal-cliente-frontend v1.
 */
export interface AdministradoraParametro {
  sParametro: string;
  sValor: string | null;
  sTipo: string | null;
  sDscParametro: string | null;
  sTamanhoImagem: string | null;
}

/**
 * Agregado de configuração do tenant (Administradora), carregado no boot a
 * partir do slug presente na URL (primeiro segmento da rota, ver
 * TenantResolver). CONFIRMADO via resposta real (tenant conectaplan,
 * 24/08/2026): os campos fixos do cadastro (CNPJ, endereço, registro ANS
 * etc.) vêm no mesmo objeto da lista de `parametros`, junto com
 * `iPermissaoAgrupada` (bitmask de permissões, checado como
 * `(iPermissaoAgrupada & flag) > 0` — ver AdministradoraService.getPermissao,
 * réplica da lógica vista no bundle minificado).
 */
export interface Administradora {
  iCodAdm: number;
  sDscAdm: string;
  sRazaoSocial: string;
  dCri: string;
  sCNPJ: string;
  sCodANS: string;
  sEnd: string;
  sEndNum: string;
  sCompl: string;
  sBairro: string;
  sCidade: string;
  sUF: string;
  sCep: string;
  sSiglaChave: string;
  iPermissaoAgrupada: number;
  parametros: AdministradoraParametro[];
  /**
   * TenantProtheus (general/GVP) — decisão de 27/08/2026: este é o
   * identificador que vai substituir `iCodAdm` quando o AuthController novo
   * (baseado no GVP) existir. Vem de `portalClienteV2`, não da Affix — não
   * confundir com `iCodAdm` (sistema próprio da Affix/Laserchip, sem
   * correspondência calculável com este campo).
   */
  tenantId?: string;
}

/**
 * Espelha o TenantResponseDTO do portal-cliente-v2-backend (host
 * `portalClienteV2`), já com `parametros` (categoria "1") embutido — decisão
 * de 27/08/2026 de combinar as duas chamadas numa só, priorizando velocidade
 * (prazo de 7 dias) sobre a separação mais cautelosa que tínhamos desenhado.
 */
export interface TenantV2Response {
  tenantNome: string;
  clienteNome: string;
  email: string;
  cnpj: string;
  codEmpresaProtheus: string | null;
  registroAns: string;
  telefoneNumero: string;
  whatsappNumero: string;
  tenantProtheus: string;
  logoUrl: string;
  site: string;
  ansImg: string;
  logoTransparenteUrl: string;
  corPrimaria: string;
  corSecundaria: string;
  emailCentralAtendimento: string;
  emailCentralVendas: string;
  emailDisparoSistemaPortalCorretor: string;
  emailDisparoSistemaPortalCliente: string;
  telefoneCentralAtendimento: string;
  telefoneCentralVendas: string;
  logoRodape: string;
  parametros: TenantParameterV2[];
}

export interface TenantParameterV2 {
  parametro: string;
  valor: string | null;
  categoria: number | null;
  tipo: string | null;
  dscParametro: string | null;
  tamanhoImagem: string | null;
}

/**
 * Nomes de `sParametro` de branding — CONFIRMADOS lendo a resposta real de
 * `ParametrosCompletos` para o tenant `conectaplan` (24/08/2026, dados de
 * configuração do tenant, não dados pessoais de usuário). Usados por
 * `LayoutService` (cores) e `AdministradoraService` (logo/fundo).
 */
export const PARAMETRO_COR_PRIMARIA = 'sCorPrimaria';
export const PARAMETRO_COR_SECUNDARIA = 'sCorSecundaria';
export const PARAMETRO_COR_TERCIARIA = 'sCorTerciaria';
export const PARAMETRO_COR_TEXTO_PRIMARIO = 'sCorTextoPrimaria';
export const PARAMETRO_COR_TEXTO_SECUNDARIO = 'sCorTextoSecundaria';
export const PARAMETRO_COR_TEXTO_TERCIARIO = 'sCorTextoTerciaria';
// `sValor` já vem como URL COMPLETA e pronta para uso (ex.:
// `https://webapilaserchip.affixbeneficios.com.br/api/v1/Arquivo/URL/5/
// {guid}.svg`) — não precisa ser montada pelo frontend.
export const PARAMETRO_LOGO = 'sUrlLogo1';
export const PARAMETRO_IMAGEM_FUNDO_LOGIN = 'sUrlFundoLogin';
